import React, { useState } from "react";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  MessageCircle,
  Search,
  Palette,
  Hammer,
  Shield,
  DollarSign,
  Truck,
  FileText,
  Clock,
  UserCheck,
  Award,
  Users,
  Calendar,
  Video,
  CreditCard,
  Phone,
  Mail,
  ChevronRight,
  HelpCircle,
  ArrowRight,
  BookOpen,
  Target,
  Eye,
  Settings,
  Star,
  Lock,
  Package,
  CheckCircle
} from "lucide-react";
import { Link } from "react-router-dom";

interface FAQCategory {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  color: string;
  questions: FAQQuestion[];
}

interface FAQQuestion {
  id: string;
  question: string;
  answer: string;
  tags?: string[];
}

const FAQs = () => {
  useSEO({
    title: "Frequently Asked Questions - Florida Highwaymen Auction",
    description: "Get answers to common questions about bidding, authentication, shipping, and collecting Florida Highwaymen art through our auction platform.",
    keywords: "FAQ, questions, answers, Florida Highwaymen, auction help, bidding guide, art authentication, shipping, collecting",
    ogImage: "/lovable-uploads/25daf720-6e00-4ba7-9642-84ee91f0e896.png"
  });
  // Inject FAQPage schema (basic)
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [] as any
    };
    // We won't serialize all questions here to avoid heavy DOM; a minimal marker is sufficient
    const id = 'ld-faqpage';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const faqCategories: FAQCategory[] = [
    {
      id: "general",
      title: "General Information",
      icon: HelpCircle,
      color: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
      questions: [
        {
          id: "what-are-highwaymen",
          question: "What are Florida Highwaymen paintings?",
          answer: "Florida Highwaymen are 26 African American landscape artists who created idyllic Florida scenes from the 1950s-1980s. They sold their paintings door-to-door throughout Florida and are now recognized as 'The Last Great American Art Movement of the 20th century.' Their works originally sold for $25 and now command thousands of dollars.",
          tags: ["history", "artists", "movement"]
        },
        {
          id: "who-are-artists",
          question: "Who are the original 26 Highwaymen artists?",
          answer: "The original 26 include founding artists like Alfred Hair (1941-1970), Harold Newton (1934-1994), Mary Ann Carroll (1940-2019), Roy McLendon (1932-2020), James Gibson (1938-2006), and others. Each brought their unique style to the Florida landscape tradition established by Alfred Hair.",
          tags: ["artists", "names", "history"]
        },
        {
          id: "platform-purpose",
          question: "What is the Florida Highwaymen Auction platform?",
          answer: "We are Florida's premier auction house dedicated exclusively to authentic Florida Highwaymen artwork. Our platform combines expert authentication, live streaming auctions, and educational content to serve collectors worldwide while preserving this important American art movement.",
          tags: ["platform", "mission", "purpose"]
        }
      ]
    },
    {
      id: "bidding",
      title: "Bidding & Auctions",
      icon: Hammer,
      color: "bg-blue-500/20 text-blue-300 border-blue-400/30",
      questions: [
        {
          id: "how-to-bid",
          question: "How do I participate in live auctions?",
          answer: "Registration is free and takes just minutes. Once registered, you can browse upcoming auctions, preview lots, and participate in live bidding through our YouTube live streams. You can also set up proxy bids to bid automatically on your behalf up to your maximum amount.",
          tags: ["bidding", "registration", "live auction"]
        },
        {
          id: "proxy-bidding",
          question: "How does proxy bidding work?",
          answer: "Proxy bidding allows you to set your maximum bid amount before or during an auction. Our system will automatically bid for you in the minimum increments necessary to maintain your winning position, up to your maximum. You'll receive notifications if you're outbid or if you win.",
          tags: ["proxy", "automatic", "bidding"]
        },
        {
          id: "bid-increments",
          question: "What are the bid increments?",
          answer: "Bid increments vary by price range: $0-$400 ($50 increments), $400-$1,400 ($100 increments), $1,400-$6,000 ($200 increments), $6,000-$12,000 ($500 increments), $12,000-$20,000 ($1,000 increments), and above $20,000 ($2,000 increments).",
          tags: ["increments", "amounts", "bidding"]
        },
        {
          id: "live-streaming",
          question: "How do live auction streams work?",
          answer: "Our auctions are broadcast live on YouTube with professional auctioneers. You can watch the stream, see current bids in real-time, participate in chat, and place bids through our platform. All bidding activity is synchronized with the live stream for a seamless experience.",
          tags: ["streaming", "youtube", "live"]
        }
      ]
    },
    {
      id: "authentication",
      title: "Authentication & Authenticity",
      icon: Shield,
      color: "bg-purple-500/20 text-purple-300 border-purple-400/30",
      questions: [
        {
          id: "authentication-process",
          question: "How do you authenticate artworks?",
          answer: "Every artwork undergoes rigorous authentication by our certified specialists using provenance research, historical sales records, artist signature analysis, painting technique examination, and consultation with recognized Highwaymen experts. We guarantee the authenticity of all pieces sold through our platform.",
          tags: ["authentication", "verification", "experts"]
        },
        {
          id: "authenticity-guarantee",
          question: "Do you guarantee authenticity?",
          answer: "Yes, we provide a lifetime authenticity guarantee on all artworks sold through our platform. If any piece is later determined to be inauthentic through accepted scholarly methods, we will provide a full refund including buyer's premium.",
          tags: ["guarantee", "authenticity", "refund"]
        },
        {
          id: "provenance-research",
          question: "What is provenance and why is it important?",
          answer: "Provenance is the ownership history of an artwork. For Highwaymen pieces, we trace the chain of ownership from the artist through previous collectors. Strong provenance increases value and confidence in authenticity, especially for works by deceased artists.",
          tags: ["provenance", "history", "ownership"]
        },
        {
          id: "condition-reports",
          question: "Do you provide condition reports?",
          answer: "Yes, detailed condition reports are available for all lots. These include high-resolution images, descriptions of any restoration or damage, and professional assessments. Condition significantly affects value, and we believe in complete transparency.",
          tags: ["condition", "reports", "transparency"]
        }
      ]
    },
    {
      id: "fees",
      title: "Fees & Payment",
      icon: DollarSign,
      color: "bg-amber-500/20 text-amber-300 border-amber-400/30",
      questions: [
        {
          id: "buyers-premium",
          question: "What are the buyer's fees?",
          answer: "As a member of our exclusive community, you enjoy reduced buyer's premiums and transparent pricing. Our fee structure is competitive and clearly disclosed before bidding. Contact us for current rates and membership benefits.",
          tags: ["fees", "premium", "membership"]
        },
        {
          id: "payment-methods",
          question: "What payment methods do you accept?",
          answer: "We accept major credit cards (Visa, MasterCard, American Express), bank wire transfers, and certified checks. Payment is due within 7 days of auction close. We also offer payment plans for purchases over $5,000.",
          tags: ["payment", "credit cards", "wire transfer"]
        },
        {
          id: "payment-plans",
          question: "Do you offer payment plans?",
          answer: "Yes, we offer flexible payment plans for purchases over $5,000. Plans typically range from 3-12 months with approved credit. A 25% deposit is required, with the balance paid in equal monthly installments.",
          tags: ["payment plans", "financing", "installments"]
        },
        {
          id: "sales-tax",
          question: "Do you charge sales tax?",
          answer: "Sales tax is charged based on your shipping address and local tax laws. Florida residents pay Florida sales tax. Out-of-state buyers may be subject to use tax in their home state. Tax calculations are handled automatically at checkout.",
          tags: ["tax", "sales tax", "state tax"]
        }
      ]
    },
    {
      id: "shipping",
      title: "Shipping & Delivery",
      icon: Truck,
      color: "bg-indigo-500/20 text-indigo-300 border-indigo-400/30",
      questions: [
        {
          id: "shipping-options",
          question: "How is artwork shipped and delivered?",
          answer: "We offer professional white-glove delivery services with full insurance coverage. Artworks are carefully packaged by our experts using museum-quality materials and shipped via trusted carriers with tracking and signature confirmation.",
          tags: ["shipping", "delivery", "packaging"]
        },
        {
          id: "shipping-costs",
          question: "What are the shipping costs?",
          answer: "Shipping costs vary by size, destination, and service level. Small works typically cost $25-75, medium works $75-150, and large works $150-300. White-glove delivery and installation services are available for an additional fee.",
          tags: ["shipping costs", "pricing", "delivery"]
        },
        {
          id: "international-shipping",
          question: "Do you ship internationally?",
          answer: "Yes, we ship worldwide. International shipping includes all necessary customs documentation. Buyers are responsible for any duties, taxes, or customs fees in their country. Shipping times vary by destination but typically take 1-3 weeks.",
          tags: ["international", "customs", "worldwide"]
        },
        {
          id: "local-pickup",
          question: "Can I pick up my purchase locally?",
          answer: "Local pickup is available at our Fort Pierce, Florida gallery by appointment. We recommend pickup for large or fragile pieces. You must provide valid ID and proof of purchase. Pickup appointments are available Monday-Friday, 9 AM-5 PM.",
          tags: ["pickup", "local", "appointment"]
        }
      ]
    },
    {
      id: "consignment",
      title: "Consignment & Selling",
      icon: FileText,
      color: "bg-pink-500/20 text-pink-300 border-pink-400/30",
      questions: [
        {
          id: "consignment-process",
          question: "How do I consign my Highwaymen artwork?",
          answer: "Submit our online consignment form with detailed photos and artwork information. Our experts will evaluate your piece within 48 hours and provide authentication, condition assessment, and market valuation. If accepted, we'll handle all aspects of the sale.",
          tags: ["consignment", "selling", "evaluation"]
        },
        {
          id: "consignment-commission",
          question: "What is your consignment commission?",
          answer: "Our consignment rates are competitive and vary based on the artwork's value and our relationship with the consigner. Rates typically range from 10-25%. We provide transparent pricing with no hidden fees and guarantee prompt payment after sale.",
          tags: ["commission", "rates", "consignment"]
        },
        {
          id: "reserve-prices",
          question: "Can I set a reserve price?",
          answer: "Yes, reserve prices protect your investment by ensuring the artwork won't sell below a predetermined amount. Reserves are typically set at 50-80% of the low estimate. Reserve information is disclosed to bidders as required by law.",
          tags: ["reserve", "minimum price", "protection"]
        },
        {
          id: "consignment-timeline",
          question: "How long does the consignment process take?",
          answer: "From acceptance to sale typically takes 2-6 months, depending on auction scheduling and market conditions. Rush consignments may be accommodated for special circumstances. Payment is issued within 35 days of auction close.",
          tags: ["timeline", "process", "payment"]
        }
      ]
    },
    {
      id: "account",
      title: "Account & Technical",
      icon: Settings,
      color: "bg-cyan-500/20 text-cyan-300 border-cyan-400/30",
      questions: [
        {
          id: "create-account",
          question: "How do I create an account?",
          answer: "Click 'Register' and provide your email, name, and create a secure password. Email verification is required. Once verified, you can immediately start bidding, save favorite lots, and access exclusive member features.",
          tags: ["registration", "account", "signup"]
        },
        {
          id: "forgot-password",
          question: "I forgot my password. How do I reset it?",
          answer: "Click 'Forgot Password' on the login page and enter your email address. You'll receive a secure reset link within minutes. If you don't see the email, check your spam folder or contact support.",
          tags: ["password", "reset", "login"]
        },
        {
          id: "update-profile",
          question: "How do I update my profile information?",
          answer: "Log in and go to 'My Profile' in your dashboard. You can update your contact information, shipping addresses, payment methods, and notification preferences. Changes are saved automatically.",
          tags: ["profile", "update", "information"]
        },
        {
          id: "notifications",
          question: "How do I manage email notifications?",
          answer: "In your account settings, you can customize which notifications you receive: bid confirmations, outbid alerts, auction reminders, new lot alerts, and newsletter updates. You can opt out of any category while keeping important account notifications.",
          tags: ["notifications", "email", "preferences"]
        }
      ]
    }
  ];

  // Filter questions based on search term and category
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(q => {
      const matchesSearch = searchTerm === "" || 
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === "all" || selectedCategory === category.id;
      
      return matchesSearch && matchesCategory;
    })
  })).filter(category => category.questions.length > 0);

  const totalQuestions = faqCategories.reduce((sum, category) => sum + category.questions.length, 0);
  const filteredQuestions = filteredCategories.reduce((sum, category) => sum + category.questions.length, 0);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative">
        {/* Animated background lighting effects */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute top-20 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"
            initial={{ scale: 1, opacity: 0.3 }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute bottom-20 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
            initial={{ scale: 1, opacity: 0.3 }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: 2,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Hero Section */}
        <section className="py-24 relative z-10">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <motion.div 
                className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <MessageCircle className="h-5 w-5 mr-3 text-emerald-400" />
                <span className="text-white font-light tracking-wide">Comprehensive Help Center</span>
              </motion.div>
              
              <motion.h1 
                className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 text-white tracking-tight leading-tight"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="block">Frequently Asked</span>
                <span className="block bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Questions
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-2xl text-white max-w-5xl mx-auto leading-relaxed font-light mb-12"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Everything you need to know about collecting <span className="font-semibold text-emerald-400">Florida Highwaymen art</span> and 
                participating in our exclusive auctions
              </motion.p>

              {/* Search and Filter */}
              <motion.div 
                className="max-w-4xl mx-auto space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
                  <Input
                    placeholder="Search questions, topics, or keywords..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 h-14 text-lg bg-white/10 backdrop-blur-xl border-white/20 text-white placeholder-white/60 focus:border-emerald-400/50 focus:ring-emerald-400/20 rounded-xl shadow-lg"
                  />
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap gap-3 justify-center">
                  <Button
                    variant={selectedCategory === "all" ? "default" : "outline"}
                    onClick={() => setSelectedCategory("all")}
                    className={selectedCategory === "all" 
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" 
                      : "bg-white/10 backdrop-blur-xl border-white/20 text-white hover:bg-white/20 rounded-xl"
                    }
                  >
                    All Categories ({totalQuestions})
                  </Button>
                  {faqCategories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <Button
                        key={category.id}
                        variant={selectedCategory === category.id ? "default" : "outline"}
                        onClick={() => setSelectedCategory(category.id)}
                        className={selectedCategory === category.id
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                          : "bg-white/10 backdrop-blur-xl border-white/20 text-white hover:bg-white/20 rounded-xl"
                        }
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {category.title} ({category.questions.length})
                      </Button>
                    );
                  })}
                </div>

                {/* Results Count */}
                {searchTerm && (
                  <div className="text-center">
                    <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-400/30 px-4 py-2">
                      {filteredQuestions} question{filteredQuestions !== 1 ? 's' : ''} found
                    </Badge>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="pb-16 relative z-10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {filteredCategories.length === 0 ? (
                <motion.div 
                  className="text-center py-16"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-12 shadow-lg">
                    <MessageCircle className="w-20 h-20 text-white/60 mx-auto mb-6" />
                    <h3 className="text-3xl font-bold text-white mb-4">No questions found</h3>
                    <p className="text-white/80 text-lg mb-8">Try adjusting your search or browse all categories</p>
                    <Button 
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('all');
                      }} 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      View All Questions
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <div className="space-y-12">
                  {filteredCategories.map((category, categoryIndex) => {
                    const Icon = category.icon;
                    return (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                      >
                        <div className="mb-8">
                          <div className="flex items-center gap-4 mb-6">
                            <div className={`p-3 rounded-xl backdrop-blur-xl ${category.color} border shadow-lg`}>
                              <Icon className="h-6 w-6" />
                            </div>
                            <div>
                              <h2 className="text-3xl font-bold text-white">
                                {category.title}
                              </h2>
                              <p className="text-white/70">
                                {category.questions.length} question{category.questions.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>

                          <Accordion type="single" collapsible className="space-y-4">
                            {category.questions.map((question, questionIndex) => (
                              <AccordionItem
                                key={question.id}
                                value={question.id}
                                className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg overflow-hidden"
                              >
                                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-white/5 transition-colors">
                                  <div className="flex items-start gap-4 w-full">
                                    <div className="bg-emerald-500/20 p-2 rounded-xl flex-shrink-0 mt-1">
                                      <HelpCircle className="h-4 w-4 text-emerald-300" />
                                    </div>
                                    <span className="text-lg font-semibold text-white text-left">
                                      {question.question}
                                    </span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-6">
                                  <div className="ml-12">
                                    <div className="prose prose-invert max-w-none">
                                      <p className="text-white/80 leading-relaxed text-base">
                                        {question.answer}
                                      </p>
                                    </div>
                                    {question.tags && (
                                      <div className="flex gap-2 mt-4 flex-wrap">
                                        {question.tags.map((tag) => (
                                          <Badge
                                            key={tag}
                                            className="bg-blue-500/20 text-blue-300 border-blue-400/30 text-xs"
                                          >
                                            {tag}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                        {categoryIndex < filteredCategories.length - 1 && <Separator className="my-12 bg-white/10" />}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 relative z-10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div 
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl font-bold text-white mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-xl text-white/80 leading-relaxed">
                  Our expert team is here to help you navigate the world of Florida Highwaymen art collecting
                </p>
              </motion.div>

              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Contact Card */}
                <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <div className="bg-emerald-500/20 p-4 rounded-xl w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <MessageCircle className="h-8 w-8 text-emerald-300" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">
                        Contact Our Experts
                      </h3>
                      <p className="text-white/70 mb-6">
                        Get personalized assistance from our certified art specialists
                      </p>
                      <Link to="/contact">
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white w-full">
                          <Mail className="mr-2 h-4 w-4" />
                          Send a Message
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* Phone Card */}
                <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <div className="bg-blue-500/20 p-4 rounded-xl w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Phone className="h-8 w-8 text-blue-300" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">
                        Call Us Directly
                      </h3>
                      <p className="text-white/70 mb-6">
                        Speak with our team for immediate assistance
                      </p>
                      <a href="tel:+1234567890">
                        <Button className="bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20 w-full">
                          <Phone className="mr-2 h-4 w-4" />
                          (555) 123-4567
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Support Hours */}
              <motion.div 
                className="mt-12 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6 max-w-2xl mx-auto shadow-lg">
                  <h4 className="text-lg font-semibold text-white mb-4">Support Hours</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/70">
                    <div className="flex items-center justify-center gap-2">
                      <Clock className="h-4 w-4 text-emerald-400" />
                      <span>Monday-Friday: 9 AM - 6 PM EST</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Clock className="h-4 w-4 text-emerald-400" />
                      <span>Saturday: 10 AM - 4 PM EST</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Mail className="h-4 w-4 text-emerald-400" />
                      <span>Email Support: 24/7</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Quick Links */}
              <motion.div 
                className="mt-12 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <h4 className="text-xl font-semibold text-white mb-6">Quick Links</h4>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Link to="/auctions">
                    <Button className="bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20">
                      <Hammer className="mr-2 h-4 w-4" />
                      Current Auctions
                    </Button>
                  </Link>
                  <Link to="/consign">
                    <Button className="bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20">
                      <FileText className="mr-2 h-4 w-4" />
                      Consign Artwork
                    </Button>
                  </Link>
                  <Link to="/artists">
                    <Button className="bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20">
                      <Users className="mr-2 h-4 w-4" />
                      Artist Profiles
                    </Button>
                  </Link>
                  <Link to="/blog">
                    <Button className="bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20">
                      <BookOpen className="mr-2 h-4 w-4" />
                      Educational Blog
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default FAQs; 